import { useMemo } from 'react';
import { CreditCard, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/Table';
import {
  MobileListCard,
  MobileListCardStack,
} from '@/components/ui/MobileListCard';
import { useIsMobile } from '@/lib/useMediaQuery';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { formatCurrency, formatRelative } from '@/lib/formatters';
import {
  useBankAccounts,
  useCards,
  useChartOfAccounts,
  useUpdateBankAccountQbMapping,
} from '@/features/financials/hooks';
import type { Tables } from '@/types/database';

// Mercury surfaces credit lines as accounts with `account_type = 'credit'`.
// Anything else (`checking`, `savings`, `depository`, …) is treated as a
// cash-bearing deposit account for the purposes of the "Total cash" tile.
function isCreditAccount(t: string | null | undefined): boolean {
  return (t ?? '').toLowerCase().includes('credit');
}

// Sentinel — Radix Select can't carry an empty string as a value, so we
// round-trip "no QBO mapping" through this constant.
const QB_NONE = '__none__';

// Only Bank / CreditCard subtypes from QBO are valid posting destinations
// for Atlas bank_accounts. We classify by QBO's `AccountSubType` (stored on
// chart_of_accounts.subtype) since QBO's own `AccountType` strings — "Bank"
// and "Credit Card" — don't all map cleanly to our enum (qbo-sync currently
// routes "Credit Card" to 'asset' because it doesn't contain "liability"),
// and we don't want this filter to be sensitive to that classification.
//
// Reference: QBO AccountSubType enum — Bank subtypes include CashOnHand,
// Checking, MoneyMarket, RentsHeldInTrust, Savings, TrustAccounts,
// CashAndCashEquivalents, OtherEarMarkedBankAccounts. Credit Card has a
// single subtype: CreditCard.
const QBO_BANK_SUBTYPES = new Set([
  'cashonhand',
  'checking',
  'moneymarket',
  'rentsheldintrust',
  'savings',
  'trustaccounts',
  'cashandcashequivalents',
  'otherearmarkedbankaccounts',
]);

function classifyQboBankish(
  a: Tables<'chart_of_accounts'>,
): 'bank' | 'credit' | null {
  if (!a.is_active) return null;
  const sub = (a.subtype ?? '').toLowerCase().replace(/[\s_-]/g, '');
  if (sub === 'creditcard') return 'credit';
  if (QBO_BANK_SUBTYPES.has(sub)) return 'bank';
  // Fallback for older/edge data without a recognised subtype: fall back to
  // the heuristic the page previously used so nothing already-mapped
  // disappears from the picker.
  if (a.account_type === 'asset' && (sub.includes('bank') || sub.includes('cash'))) return 'bank';
  if (a.account_type === 'liability' && (sub.includes('credit') || sub.includes('card'))) {
    return 'credit';
  }
  return null;
}

export function BankAccountsPage() {
  const isMobile = useIsMobile();
  const { data: accounts, isLoading } = useBankAccounts();
  const { data: cards, isLoading: cardsLoading } = useCards();
  const { data: coa } = useChartOfAccounts();
  const updateMapping = useUpdateBankAccountQbMapping();

  const depositAccounts = (accounts ?? []).filter((a) => !isCreditAccount(a.account_type));
  const creditAccounts = (accounts ?? []).filter((a) => isCreditAccount(a.account_type));

  const totalCash = depositAccounts.reduce((acc, a) => acc + a.current_balance_cents, 0);
  const totalCredit = creditAccounts.reduce((acc, a) => acc + a.current_balance_cents, 0);

  const { qboBankAccounts, qboCreditAccounts } = useMemo(() => {
    const banks: Tables<'chart_of_accounts'>[] = [];
    const credits: Tables<'chart_of_accounts'>[] = [];
    for (const a of coa ?? []) {
      const kind = classifyQboBankish(a);
      if (kind === 'bank') banks.push(a);
      else if (kind === 'credit') credits.push(a);
    }
    const sorter = (x: Tables<'chart_of_accounts'>, y: Tables<'chart_of_accounts'>) => {
      const xc = x.code ?? '';
      const yc = y.code ?? '';
      if (xc && yc && xc !== yc) return xc.localeCompare(yc);
      return x.name.localeCompare(y.name);
    };
    banks.sort(sorter);
    credits.sort(sorter);
    return { qboBankAccounts: banks, qboCreditAccounts: credits };
  }, [coa]);

  const qboLabelById = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of coa ?? []) m.set(a.id, formatQbAccountLabel(a));
    return m;
  }, [coa]);

  const onChangeMapping = async (acctId: string, nextValue: string) => {
    const qb_account_id = nextValue === QB_NONE ? null : nextValue;
    try {
      await updateMapping.mutateAsync({ id: acctId, qb_account_id });
      toast.success(qb_account_id ? 'QBO mapping updated' : 'QBO mapping cleared');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Update failed';
      toast.error(msg);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Accounts"
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Total cash</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-40" />
            ) : (
              <p className="text-3xl font-medium tracking-headline tabular-nums">
                {formatCurrency(totalCash)}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Credit balance</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-40" />
            ) : (
              <p className="text-3xl font-medium tracking-headline tabular-nums">
                {formatCurrency(totalCredit)}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Bank & credit accounts</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : (accounts ?? []).length === 0 ? (
            <EmptyState
              icon={Wallet}
              title="No accounts yet"
            />
          ) : isMobile ? (
            // Mobile: stack each account as a `MobileListCard` with the
            // name as the title, current balance prominently on the
            // right (tone-coloured), and a divided full-width "Posts
            // to (QBO)" picker below — the operator needs touch-friendly
            // hit targets for the Select trigger and can't fish for it
            // inside a horizontal-scrolling table.
            <MobileListCardStack>
              {(accounts ?? []).map((a) => {
                const credit = isCreditAccount(a.account_type);
                const candidatePool = credit ? qboCreditAccounts : qboBankAccounts;
                const meta: { label: string; value: React.ReactNode }[] = [];
                if (a.available_balance_cents !== null && !credit) {
                  meta.push({
                    label: 'Available',
                    value: (
                      <span className="tabular-nums">
                        {formatCurrency(a.available_balance_cents)}
                      </span>
                    ),
                  });
                }
                if (a.last_synced_at) {
                  meta.push({
                    label: 'Last sync',
                    value: formatRelative(a.last_synced_at),
                  });
                }
                return (
                  <div
                    key={a.id}
                    className="space-y-2 rounded-lg border border-border bg-card p-3.5"
                  >
                    <MobileListCard
                      title={a.name}
                      subtitle={
                        <span>
                          {a.institution}{' '}
                          <Badge variant="muted" className="ml-1">
                            {a.provider}
                          </Badge>
                        </span>
                      }
                      badges={
                        <>
                          {credit ? (
                            <Badge variant="info">Credit</Badge>
                          ) : (
                            <Badge variant="muted" className="capitalize">
                              {a.account_type ?? 'Deposit'}
                            </Badge>
                          )}
                        </>
                      }
                      amount={{
                        value: formatCurrency(a.current_balance_cents),
                        caption: credit ? 'Owed' : 'Current',
                        tone: credit
                          ? 'negative'
                          : a.current_balance_cents >= 0
                            ? 'positive'
                            : 'negative',
                      }}
                      meta={meta}
                      trailing={null}
                      className="border-0 bg-transparent p-0 active:scale-100"
                    />
                    <div className="space-y-1.5 border-t border-border pt-2.5">
                      <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                        Posts to (QBO)
                      </div>
                      <QboMappingPicker
                        value={a.qb_account_id}
                        options={candidatePool}
                        fallbackLabel={
                          a.qb_account_id
                            ? qboLabelById.get(a.qb_account_id) ?? '—'
                            : null
                        }
                        disabled={
                          updateMapping.isPending || (coa ?? []).length === 0
                        }
                        onChange={(v) => onChangeMapping(a.id, v)}
                        credit={credit}
                        coaSynced={(coa ?? []).length > 0}
                      />
                    </div>
                  </div>
                );
              })}
            </MobileListCardStack>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Institution</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Available</TableHead>
                  <TableHead className="text-right">Current</TableHead>
                  <TableHead className="w-72">Posts to (QBO)</TableHead>
                  <TableHead>Last sync</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(accounts ?? []).map((a) => {
                  const credit = isCreditAccount(a.account_type);
                  const candidatePool = credit ? qboCreditAccounts : qboBankAccounts;
                  return (
                    <TableRow key={a.id}>
                      <TableCell className="font-medium">{a.name}</TableCell>
                      <TableCell>
                        {a.institution} <Badge variant="muted" className="ml-1">{a.provider}</Badge>
                      </TableCell>
                      <TableCell>
                        {credit ? (
                          <Badge variant="info">Credit</Badge>
                        ) : (
                          <span className="capitalize text-muted-foreground">
                            {a.account_type ?? '—'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCurrency(a.available_balance_cents ?? a.current_balance_cents)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCurrency(a.current_balance_cents)}
                      </TableCell>
                      <TableCell>
                        <QboMappingPicker
                          value={a.qb_account_id}
                          options={candidatePool}
                          fallbackLabel={
                            a.qb_account_id ? qboLabelById.get(a.qb_account_id) ?? '—' : null
                          }
                          disabled={updateMapping.isPending || (coa ?? []).length === 0}
                          onChange={(v) => onChangeMapping(a.id, v)}
                          credit={credit}
                          coaSynced={(coa ?? []).length > 0}
                        />
                      </TableCell>
                      <TableCell className="text-muted-foreground text-xs">
                        {a.last_synced_at ? formatRelative(a.last_synced_at) : '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Cards</CardTitle>
        </CardHeader>
        <CardContent>
          {cardsLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : (cards ?? []).length === 0 ? (
            <EmptyState
              icon={CreditCard}
              title="No cards yet"
            />
          ) : isMobile ? (
            <MobileListCardStack>
              {(cards ?? []).map((c) => (
                <MobileListCard
                  key={c.id}
                  leading={
                    <div className="flex h-9 w-9 items-center justify-center rounded-md border border-border bg-secondary/40 text-muted-foreground">
                      <CreditCard className="h-4 w-4" />
                    </div>
                  }
                  title={c.name_on_card ?? '—'}
                  subtitle={
                    <span className="tabular-nums">
                      •••• {c.last_four ?? '????'}
                      {c.network ? ` · ${c.network}` : ''}
                    </span>
                  }
                  badges={
                    <CardStatusBadge
                      status={c.status}
                      physical={c.physical_card_status}
                    />
                  }
                  meta={[
                    {
                      label: 'Account',
                      value: c.bank_accounts?.name ?? '—',
                    },
                    {
                      label: 'Issued',
                      value: c.issued_at ? formatRelative(c.issued_at) : '—',
                    },
                  ]}
                  trailing={null}
                />
              ))}
            </MobileListCardStack>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cardholder</TableHead>
                  <TableHead>Card</TableHead>
                  <TableHead>Network</TableHead>
                  <TableHead>Account</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Issued</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(cards ?? []).map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium">{c.name_on_card ?? '—'}</TableCell>
                    <TableCell className="tabular-nums text-muted-foreground">
                      •••• {c.last_four ?? '????'}
                    </TableCell>
                    <TableCell className="capitalize text-muted-foreground">
                      {c.network ?? '—'}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {c.bank_accounts?.name ?? '—'}
                    </TableCell>
                    <TableCell>
                      <CardStatusBadge status={c.status} physical={c.physical_card_status} />
                    </TableCell>
                    <TableCell className="text-muted-foreground text-xs">
                      {c.issued_at ? formatRelative(c.issued_at) : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function QboMappingPicker({
  value,
  options,
  fallbackLabel,
  onChange,
  disabled,
  credit,
  coaSynced,
}: {
  value: string | null;
  options: Tables<'chart_of_accounts'>[];
  fallbackLabel: string | null;
  onChange: (next: string) => void;
  disabled: boolean;
  credit: boolean;
  coaSynced: boolean;
}) {
  // Defensive: if the saved qb_account_id is for a row that doesn't pass
  // classifyQboBankish() (e.g. someone changed its subtype in QBO) we
  // still want to surface the current value so the user can fix it.
  const valueInOptions = !value || options.some((o) => o.id === value);
  const augmented =
    !valueInOptions && value
      ? [
          ...options,
          { id: value, name: fallbackLabel ?? '(unrecognized)', code: null } as Pick<
            Tables<'chart_of_accounts'>,
            'id' | 'name' | 'code'
          >,
        ]
      : options;

  return (
    <Select
      value={value ?? QB_NONE}
      onValueChange={onChange}
      disabled={disabled}
    >
      <SelectTrigger className="h-9 w-full">
        <SelectValue
          placeholder={coaSynced ? 'Map to QBO account…' : 'Sync QuickBooks first…'}
        />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={QB_NONE}>
          <span className="text-muted-foreground">Unmapped</span>
        </SelectItem>
        <SelectSeparator />
        <SelectGroup>
          <SelectLabel>{credit ? 'QBO Credit Card accounts' : 'QBO Bank accounts'}</SelectLabel>
          {augmented.length === 0 ? (
            <div className="px-3 py-1.5 text-xs text-muted-foreground">
              No QBO {credit ? 'credit card' : 'bank'} accounts found.
            </div>
          ) : (
            augmented.map((a) => (
              <SelectItem key={a.id} value={a.id}>
                {formatQbAccountLabel(a)}
              </SelectItem>
            ))
          )}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}

function formatQbAccountLabel(
  acct: Pick<Tables<'chart_of_accounts'>, 'code' | 'name'>,
): string {
  return acct.code ? `${acct.code} · ${acct.name}` : acct.name;
}

function CardStatusBadge({
  status,
  physical,
}: {
  status: string | null;
  physical: string | null;
}) {
  if (!status) return <span className="text-muted-foreground">—</span>;
  // A "frozen", "cancelled", "expired", or "suspended" card should jump out
  // visually so super_admins notice it next to the other green/active rows.
  const variant: 'success' | 'warning' | 'muted' =
    status === 'active'
      ? 'success'
      : status === 'frozen' || status === 'inactive' || status === 'suspended'
        ? 'warning'
        : 'muted';
  return (
    <span className="inline-flex items-center gap-1.5">
      <Badge variant={variant} className="capitalize">
        {status}
      </Badge>
      {physical && physical !== 'active' && (
        <Badge variant="outline" className="capitalize">
          phys: {physical}
        </Badge>
      )}
    </span>
  );
}
